import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
} from 'reactstrap';
import Select from 'react-select';

import styles from 'pages/Promotions/styles/promo.module.css';

import { useTranslation } from 'react-i18next';

import { PromoFormValues } from 'types/promotion/promotionTypes';
import { FormikProps } from 'formik';

import {
  PROMO_PRODUCTS_LIMIT,
  PROMO_CATEGORY_LIMIT,
  TypeConditionsOptions,
} from 'pages/Promotions/PromoConstant';
import { useCallback, useEffect, useState } from 'react';
import { RootState } from 'slices';
import { useSelector } from 'react-redux';
import { MultiSelectStyles } from 'styles/common/multiSelectStyle';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { FaImage } from 'react-icons/fa';
import { transformProductData2Options } from 'utils/mappers/productMapper';
import {
  CheckboxOption,
  CheckboxOptionWithImg,
} from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';
import {
  InputActionMeta,
  SelectedData,
  SelectOptions,
} from 'types/common/FormTypes';
import {
  setProductCategoryOptions,
  setselectMultiCategories,
  setselectMultiProducts,
} from 'slices/promotions/reducer';
import { convertToProductCategoryOptions } from 'utils/products/converter';
import {
  getSearchProductsCategory,
  getSearchProductsList,
} from 'services/productService';
import { ImageWithFallback } from 'Components/pos2u/ImageWithFallback';

const PromoProducts = ({
  formik,
}: {
  formik: FormikProps<PromoFormValues>;
}) => {
  const { t } = useTranslation();

  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const promotionsState = useSelector((state: RootState) => state.Promotions);
  const layoutState = useSelector((state: RootState) => state.Layout);
  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded } = infoState;

  const {
    showAllValidateErrors,
    ProductCategoryOptions,
    selectMultiCategories,
    selectMultiProducts,
  } = promotionsState;
  const { layoutModeType } = layoutState;

  const isDarkMode = layoutModeType === 'dark';

  const [inputValue, setInputValue] = useState('');
  const [inputProductValue, setInputProductValue] = useState('');
  const [productOptions, setProductOptions] = useState<SelectOptions[]>([]);

  const [isCategory, setIsCategory] = useState(false);
  const [isProducts, setIsProducts] = useState(false);
  const [showExceedCategoryLimitError, setShowExceedCategoryLimitError] =
    useState(false);
  const [showExceedLimitError, setShowExceedLimitError] = useState(false);

  const fetchProductCategories = useCallback(async () => {
    if (!merchant?.id) return;

    try {
      const response = await getSearchProductsCategory(`[${merchant.id}]`);

      if (response) {
        const { message, error_code, data } = response;

        if (message === 'OK' && error_code === 0) {
          const categoryOptions = data.categories
            ? await convertToProductCategoryOptions(data.categories)
            : [{ options: [] }];
          dispatch(setProductCategoryOptions(categoryOptions));
        } else {
          dispatch(setProductCategoryOptions([{ options: [] }]));
        }
      } else {
        console.error('No response data returned');

        dispatch(setProductCategoryOptions([{ options: [] }]));
      }
    } catch (error) {
      console.error('Failed to fetch product categories:', error);
      dispatch(setProductCategoryOptions([{ options: [] }]));
    }
  }, [merchant?.id, dispatch]);

  useEffect(() => {
    if (isMerchantLoaded && merchant?.id) {
      fetchProductCategories();
    }
  }, [isMerchantLoaded, merchant, fetchProductCategories]);

  useEffect(() => {
    setIsProducts(formik.values.ConditionType === '1');
    setIsCategory(formik.values.ConditionType === '2');
  }, [formik.values?.ConditionType, setIsCategory, setIsProducts]);

  const transformOptionsToListsArray = (
    data: SelectedData[] | null | undefined
  ) => {
    const lists: string[] = [];
    if (data && Array.isArray(data)) {
      data.forEach(({ value }) => {
        lists.push(value);
      });
    }
    return lists;
  };
  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const handleSearch = useCallback(
    async (term: string) => {
      if (!isMerchantLoaded || !merchant?.id || term.length < 3) return;

      try {
        //! FIXME: This value is currently fixed for testing. Update this logic in the future.
        const results = await getSearchProductsList(`${merchant.id}`, term);

        if (results) {
          const { message, error_code, data } = results;

          if (message === 'OK' && error_code === 0) {
            if (data && data.products) {
              const searchResultsOptions = await transformProductData2Options(
                data.products
              );
              setProductOptions(searchResultsOptions[0].options);
            } else {
              setProductOptions([]);
            }
          } else {
            setProductOptions([]);
          }
        } else {
          console.error('No response data returned');
          setProductOptions([]);
        }
      } catch (error) {
        console.log(error);
        setProductOptions([]);
      }
    },
    [merchant?.id, isMerchantLoaded]
  );
  const handleInputProductChange = (newValue: string) => {
    setInputProductValue(newValue);
    handleSearch(newValue);
  };

  const handleChangeConditionType = (
    e: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<PromoFormValues>
  ) => {
    formik.handleChange(e);
    setShowExceedCategoryLimitError(false);
    setShowExceedLimitError(false);
    formik
      .setFieldValue(`promoProducts.lists`, [])
      .then(() => formik.setFieldTouched(`promoProducts.lists`, true));
    if (e.target.value === '2') {
      setIsCategory(true);
      setIsProducts(false);
      dispatch(setselectMultiProducts([]));
    } else if (e.target.value === '1') {
      setIsProducts(true);
      setIsCategory(false);
      dispatch(setselectMultiCategories([]));
    } else {
      setIsProducts(false);
      setIsCategory(false);
      dispatch(setselectMultiProducts([]));
      dispatch(setselectMultiCategories([]));
    }
  };

  const handleChangePromoCategory = (selectedOptions: SelectedData[]) => {
    if (selectedOptions.some((option) => option.value === 'select_all')) {
      const allOptions = ProductCategoryOptions[0].options;
      if (allOptions.length > PROMO_CATEGORY_LIMIT) {
        setShowExceedCategoryLimitError(true);
        return;
      }

      setShowExceedCategoryLimitError(false);
      dispatch(setselectMultiCategories(allOptions));
      formik
        .setFieldValue(
          `promoProducts.lists`,
          transformOptionsToListsArray(allOptions)
        )
        .then(() => formik.setFieldTouched(`promoProducts.lists`, true));
    } else {
      if (selectedOptions.length > PROMO_CATEGORY_LIMIT) {
        setShowExceedCategoryLimitError(true);
        return;
      }

      setShowExceedCategoryLimitError(false);
      dispatch(setselectMultiCategories(selectedOptions));
      formik
        .setFieldValue(
          `promoProducts.lists`,
          transformOptionsToListsArray(selectedOptions)
        )
        .then(() => formik.setFieldTouched(`promoProducts.lists`, true));
    }
  };

  const handleChangePromoProduct = (selectedOptions: SelectedData[]) => {
    if (selectedOptions.length > PROMO_PRODUCTS_LIMIT) {
      setShowExceedLimitError(true);
      return;
    }
    setShowExceedLimitError(false);
    dispatch(setselectMultiProducts(selectedOptions));
    formik
      .setFieldValue(
        `promoProducts.lists`,
        transformOptionsToListsArray(selectedOptions)
      )
      .then(() => formik.setFieldTouched(`promoProducts.lists`, true));
  };

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{t('Promotional_Products')}</h5>
      </CardHeader>
      <CardBody className={`${styles['card-body-pd']}`}>
        <Row>
          <Col lg={6}>
            <div className="mb-3 row">
              <Label
                htmlFor="ConditionType"
                className="col-sm-3 col-form-label"
              >
                {t('On_Purchase')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="mt-2">
                  <FormGroup key={4}>
                    {TypeConditionsOptions(t).map(
                      (p: { label: string; value: string }, _index) => (
                        <div
                          key={p.value}
                          className="form-check form-check-inline"
                        >
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="ConditionType"
                            id={p.label}
                            value={p.value}
                            checked={formik.values.ConditionType === p.value}
                            onChange={(e) => {
                              handleChangeConditionType(e, formik);
                            }}
                          />
                          <Label className="form-check-label" htmlFor={p.label}>
                            {p.label}
                          </Label>
                        </div>
                      )
                    )}
                  </FormGroup>
                </div>
              </Col>
            </div>

            <div
              className={`mb-3 row ${isCategory ? '' : 'd-none'}`}
              data-testid="pp-category"
            >
              <Label
                htmlFor="ConditionType"
                className="col-sm-3 col-form-label"
              ></Label>
              <Col sm={9}>
                <Select
                  id="promoProducts.category"
                  name="promoProducts.category"
                  aria-label="pp-category"
                  value={selectMultiCategories}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={handleChangePromoCategory}
                  options={ProductCategoryOptions}
                  components={{ Option: CheckboxOption }}
                  styles={MultiSelectStyles(isDarkMode)}
                  placeholder={t('PleaseSelect')}
                />
                {showExceedCategoryLimitError ? (
                  <div className="text-danger mt-1">
                    {`${t('Cannot select more than')} ${PROMO_CATEGORY_LIMIT} ${t(
                      'text_items'
                    )}`}
                  </div>
                ) : (formik.touched.promoProducts?.lists &&
                    formik.errors.promoProducts?.lists &&
                    typeof formik.errors.promoProducts.lists === 'string') ||
                  (formik.isValid === false &&
                    formik.errors.promoProducts?.lists &&
                    showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoProducts.lists}
                  </div>
                ) : null}
              </Col>
            </div>

            <div
              className={`mb-3 row ${isProducts ? '' : 'd-none'}`}
              data-testid="pp-products"
            >
              <Label
                htmlFor="ConditionType"
                className="col-sm-3 col-form-label"
              ></Label>
              <Col sm={9}>
                <Select
                  id="promoProducts.products"
                  name="promoProducts.products"
                  aria-label="pp-products"
                  components={{ Option: CheckboxOptionWithImg }}
                  value={selectMultiProducts}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  onChange={handleChangePromoProduct}
                  inputValue={inputProductValue}
                  onInputChange={handleInputProductChange}
                  options={productOptions}
                  formatOptionLabel={(option: SelectedData) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: '2rem',
                          height: '2rem',
                          overflow: 'hidden',
                          display: 'inline-block',
                          marginRight: '10px',
                          transition: 'transform 0.3s ease',
                        }}
                        className="image-container"
                      >
                        {option.image && option.image !== '' ? (
                          <ImageWithFallback
                            src={option.image}
                            alt={option.label}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease',
                            }}
                          />
                        ) : (
                          <FaImage
                            style={{
                              fontSize: '2rem',
                              marginRight: '10px',
                            }}
                            color="gray"
                          />
                        )}
                      </div>
                      {option.label}
                    </div>
                  )}
                  styles={MultiSelectStyles(isDarkMode)}
                  placeholder={t('PleaseSelect')}
                />
                {showExceedLimitError ? (
                  <div className="text-danger mt-1">
                    {`${t('Cannot select more than')} ${PROMO_PRODUCTS_LIMIT} ${t(
                      'text_items'
                    )}`}
                  </div>
                ) : (formik.touched.promoProducts?.lists &&
                    formik.errors.promoProducts?.lists &&
                    typeof formik.errors.promoProducts.lists === 'string') ||
                  (formik.isValid === false &&
                    formik.errors.promoProducts?.lists &&
                    showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoProducts.lists}
                  </div>
                ) : null}
              </Col>
            </div>
          </Col>

          <Col lg={6}></Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default PromoProducts;
